import {
  type BrandColors,
  getCalculatedStyleProperties,
  type HSLString,
  hslToHex,
  setGlobalStyleProperties,
  TURNIT_BRAND_COLORS,
} from '@fleet/design-tokens';

import type { TenantOption } from '@/types/tenant';
import { getTenantConfiguration } from '@/utils/tenant';

// Re-export the design-tokens colour helpers the rest of the engine consumes,
// so there is a single import surface and no duplicated colour maths.
export { getCalculatedStyleProperties, hslToHex };

// design-tokens does not emit a `white` token, so keep it as a local constant.
export const WHITE = '0, 0%, 100%' as HSLString;

export const decodeBase64SVG = (
  base64: string,
  background: HSLString,
  outline: HSLString
) => {
  const decoded = atob(base64);

  let count = 0;

  const updatedSvgString = decoded.replace(/fill="[^"]*"/g, () => {
    let newFill;

    if (count === 0) {
      newFill = 'fill="none"';
    } else if (count === 1) {
      newFill = `fill="${hslToHex(background)}"`;
    } else {
      newFill = `fill="${hslToHex(outline)}"`;
    }

    count++;
    return newFill;
  });

  return btoa(updatedSvgString);
};

export const getTenantBrand = (
  tenant: TenantOption
): { brandColors: BrandColors } => {
  const tenantConfiguration = getTenantConfiguration(tenant.value);

  return tenantConfiguration ?? { brandColors: TURNIT_BRAND_COLORS };
};

export const setTenantBrand = (tenant: TenantOption) => {
  const { brandColors } = getTenantBrand(tenant);

  const calculatedStyleProperties = getCalculatedStyleProperties(brandColors);

  // `background` and `background-text` are brand-defined tokens emitted
  // directly by design-tokens (no inference) — `setGlobalStyleProperties`
  // writes them straight to `--background` / `--background-text`.
  setGlobalStyleProperties(calculatedStyleProperties);

  // Page content uses the brand on-background text colour by default.
  document.body.classList.remove('text-white', 'text-dark');
  document.body.classList.add('text-background-text');
};

export const setTenantFavicon = (tenant: TenantOption) => {
  const favicon = document.head.querySelector<HTMLLinkElement>(
    'link#tenant-favicon'
  );

  if (favicon) {
    favicon.href = `/${tenant.value}.png`;
  }
};
